import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export interface CurriculumClass { id: number; class_label: string; }
export interface CurriculumGroup { curriculum_id: number; curriculum_name: string; classes: CurriculumClass[]; }

interface Props {
  classGroups: CurriculumGroup[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export default function MultiClassSelect({ classGroups, selectedIds, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  const allClasses = classGroups.flatMap(g => g.classes);
  const allIds = allClasses.map(c => String(c.id));
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id));

  const filteredGroups = search.trim()
    ? classGroups
        .map(g => ({ ...g, classes: g.classes.filter(c => c.class_label.toLowerCase().includes(search.toLowerCase())) }))
        .filter(g => g.classes.length > 0)
    : classGroups;

  const toggle = (id: string) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const toggleAll = () => onChange(allSelected ? [] : allIds);
  const handleDone = () => { setOpen(false); setSearch(''); };

  const triggerLabel = selectedIds.length === 0
    ? 'All Classes'
    : selectedIds.length === 1
      ? allClasses.find(c => String(c.id) === selectedIds[0])?.class_label ?? '1 Class'
      : `${selectedIds.length} classes selected`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        title={disabled ? 'Select an academic year first' : undefined}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 38, padding: '0 12px',
          background: selectedIds.length > 0 ? 'var(--slate-soft)' : 'var(--surface)',
          border: '1.5px solid var(--border-2)',
          borderRadius: 'var(--r-input)', cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 13.5, fontWeight: selectedIds.length > 0 ? 600 : 500,
          color: selectedIds.length > 0 ? 'var(--text)' : 'var(--text-2)',
          fontFamily: 'var(--font)', letterSpacing: '-0.1px',
          whiteSpace: 'nowrap', transition: 'all .13s',
          opacity: disabled ? 0.45 : 1,
          ...(open ? { borderColor: 'var(--slate)', boxShadow: '0 0 0 3px rgba(27,34,48,0.07)' } : {}),
        }}
      >
        <span>{triggerLabel}</span>
        {selectedIds.length >= 2 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 20, height: 20, padding: '0 5px',
            background: 'var(--slate)', color: '#fff',
            borderRadius: 999, fontSize: 11, fontWeight: 700,
          }}>
            {selectedIds.length}
          </span>
        )}
        <ChevronDown size={13} style={{ color: 'var(--text-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', left: 0, zIndex: 300,
          background: '#fff', border: '1.5px solid var(--border-2)',
          borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          width: 280, display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search classes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', height: 34, paddingLeft: 30, paddingRight: 10,
                  border: '1.5px solid var(--border-2)', borderRadius: 8,
                  fontSize: 13, fontFamily: 'var(--font)', color: 'var(--text)',
                  background: 'var(--bg)', outline: 'none', transition: 'border-color .13s',
                  boxSizing: 'border-box',
                }}
                onFocus={e => (e.target.style.borderColor = '#e8b84b')}
                onBlur={e => (e.target.style.borderColor = 'var(--border-2)')}
              />
            </div>
          </div>

          {/* Select all / Clear */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px 4px' }}>
            <button type="button" onClick={toggleAll} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Select all
            </button>
            {selectedIds.length > 0 && (
              <button type="button" onClick={() => onChange([])} style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Clear
              </button>
            )}
          </div>

          {/* Class list */}
          <div style={{ overflowY: 'auto', maxHeight: 240, padding: '2px 0' }}>
            {filteredGroups.length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>No classes found</div>
            )}
            {filteredGroups.map(g => (
              <div key={g.curriculum_id}>
                {classGroups.length > 1 && (
                  <div style={{ padding: '6px 14px 2px', fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {g.curriculum_name}
                  </div>
                )}
                {g.classes.map(c => {
                  const checked = selectedIds.includes(String(c.id));
                  return (
                    <label key={c.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '7px 14px', cursor: 'pointer',
                      background: checked ? 'rgba(27,34,48,0.04)' : 'transparent',
                      transition: 'background .1s',
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(String(c.id))}
                        style={{ width: 15, height: 15, accentColor: 'var(--slate)', flexShrink: 0, cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: checked ? 600 : 500, color: checked ? 'var(--text)' : 'var(--text-2)' }}>
                        {c.class_label}
                      </span>
                    </label>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px 10px', borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>
              {selectedIds.length} of {allIds.length} selected
            </span>
            <button type="button" onClick={handleDone} style={{
              height: 30, padding: '0 14px',
              background: 'var(--slate)', color: '#fff',
              border: 'none', borderRadius: 7,
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)',
            }}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Separate chips component — render after the filters row div in each module ──
interface ChipsProps {
  classGroups: CurriculumGroup[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  style?: React.CSSProperties;
}

export function ClassFilterChips({ classGroups, selectedIds, onChange, style }: ChipsProps) {
  if (selectedIds.length < 2) return null;
  const allClasses = classGroups.flatMap(g => g.classes);
  const getLabel = (id: string) => allClasses.find(c => String(c.id) === id)?.class_label ?? id;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '8px 0 4px', ...style }}>
      {selectedIds.map(id => (
        <span key={id} style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          height: 26, padding: '0 10px',
          background: 'var(--slate-soft)', borderRadius: 999,
          fontSize: 12, fontWeight: 600, color: 'var(--text-2)', whiteSpace: 'nowrap',
        }}>
          {getLabel(id)}
          <X size={10} style={{ cursor: 'pointer', opacity: 0.6, flexShrink: 0 }} onClick={() => onChange(selectedIds.filter(x => x !== id))} />
        </span>
      ))}
      <button type="button" onClick={() => onChange([])} style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px' }}>
        Clear all
      </button>
    </div>
  );
}
